import config from '../config';
import {
  customFetch,
  authHeader,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
} from '../helpers';

const endpointPath = config.api.API_URL + '/setting';

export const settingService = {
  getAll,
  getAllForFrontend,
  getCertificateStatus,
  parseCertificates,
  update,
  sendTestEmail,
  testOidcConfiguration,
  rotateOidcSigningKey,
};

function getAll(signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/all`, requestOptions, signal).then(handleResponse);
}

/**
 * Sends a GET request for how this installation verifies TLS and which CA certificates its
 * container image supplies. Neither is a setting -- both are decided by how the container was
 * started -- so they are read separately from the settings themselves.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ ok: boolean, certificate_status: object }>}
 */
function getCertificateStatus(signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/certificate_status`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a POST request describing the certificates in a value, without storing it. Used to show
 * what has been pasted before it is saved; reading an X.509 certificate needs a parser the browser
 * does not have.
 * @param {string} value - The PEM text to describe.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ ok: boolean, certificates: object[], invalid_blocks: object[], trailing_text: boolean }>}
 */
function parseCertificates(value, signal) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), ...jsonHeader() },
    ...commonRequestOptions(),
    body: JSON.stringify({ value }),
  };
  return customFetch(`${endpointPath}/parse_certificates`, requestOptions, signal).then(
    handleResponse
  );
}

function getAllForFrontend(signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/all_for_frontend`, requestOptions, signal).then(
    handleResponse
  );
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function sendTestEmail(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/send_test_email`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a POST request asking the API to test a Microsoft Entra Id configuration.
 * @param {{ config: object }} items - The settings to test, as currently filled in on the form.
 * @param {AbortController} [signal] - Aborted to give up on a test that is taking too long.
 * @returns {Promise<{ ok: boolean, status: string, message: string, detail: string }>}
 */
function testOidcConfiguration(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/test_oidc_configuration`, requestOptions, signal).then(
    handleResponse
  );
}

function rotateOidcSigningKey(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/rotate_oidc_signing_key`, requestOptions, signal).then(
    handleResponse
  );
}
